
import { BlogPostFormData } from '@/types/blog';
import { toast } from 'sonner';

/**
 * Custom hook for blog post form validation
 */
export const useFormValidation = () => {
  const validateForm = (formData: BlogPostFormData): boolean => {
    if (!formData.title.trim()) {
      toast.error("Post title is required");
      return false;
    }

    if (formData.status === 'scheduled' && !formData.scheduled_for) {
      toast.error("Scheduled date is required for scheduled posts");
      return false;
    }

    // Validate sources if any are present
    if (formData.metadata?.sources && Array.isArray(formData.metadata.sources)) {
      const invalidSources = formData.metadata.sources.filter(
        source => !source.description.trim() || !source.url.trim()
      );
      
      if (invalidSources.length > 0) {
        toast.error("All sources must have both a description and URL");
        return false;
      }
    }

    return true;
  };

  // Ensures the form data has all required metadata fields initialized
  const normalizeFormData = (formData: BlogPostFormData): BlogPostFormData => {
    const normalizedData = { ...formData };

    // Ensure metadata exists and has all required properties
    if (!normalizedData.metadata) {
      normalizedData.metadata = { 
        author: "", 
        categories: [], 
        tags: [], 
        featured: false, 
        seo_description: "", 
        sources: [] 
      };
    } else {
      // Ensure all required metadata fields exist
      normalizedData.metadata.author = normalizedData.metadata.author || "";
      normalizedData.metadata.categories = normalizedData.metadata.categories || [];
      normalizedData.metadata.tags = normalizedData.metadata.tags || [];
      normalizedData.metadata.featured = normalizedData.metadata.featured || false;
      normalizedData.metadata.seo_description = normalizedData.metadata.seo_description || "";
      normalizedData.metadata.sources = normalizedData.metadata.sources || [];
    }

    // Generate slug if missing
    if (!normalizedData.slug && normalizedData.title) {
      normalizedData.slug = normalizedData.title.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }

    return normalizedData;
  };

  // Prepare data for Supabase (convert Dates to ISO strings)
  const prepareForSave = (formData: BlogPostFormData) => {
    const normalizedData = normalizeFormData(formData);
    
    return {
      title: normalizedData.title,
      content: normalizedData.content,
      excerpt: normalizedData.excerpt,
      image_url: normalizedData.image_url,
      slug: normalizedData.slug,
      status: normalizedData.status,
      // Convert Date to ISO string format that Supabase expects
      scheduled_for: normalizedData.status === 'scheduled' && normalizedData.scheduled_for 
        ? normalizedData.scheduled_for.toISOString() 
        : null,
      // Include published_at field when it exists
      published_at: normalizedData.published_at ? normalizedData.published_at.toISOString() : null,
      metadata: normalizedData.metadata || {},
    };
  };

  return {
    validateForm,
    normalizeFormData,
    prepareForSave
  };
};
